import Link from 'next/link';

export type StatusPageVariant = 'not-found' | 'error';

type StatusPageProps = {
  variant: StatusPageVariant;
  /** Client error boundary retry */
  onRetry?: () => void;
  /** Extra detail for unexpected errors (optional, keep short). */
  detail?: string | null;
};

const COPY: Record<
  StatusPageVariant,
  {
    code: string;
    title: string;
    description: string;
    iconClass: string;
  }
> = {
  'not-found': {
    code: '404',
    title: 'Pagina non trovata',
    description:
      'La pagina che stai cercando non esiste o è stata spostata. Controlla l’indirizzo oppure torna alla home.',
    iconClass: 'bi bi-search',
  },
  error: {
    code: 'Errore',
    title: 'Si è verificato un problema',
    description:
      'Qualcosa non ha funzionato correttamente. Puoi riprovare oppure tornare alla home.',
    iconClass: 'bi bi-exclamation-triangle',
  },
};

/**
 * Shared themed status page used by not-found / error boundaries.
 * Relies on bootstrap-italia + site tokens already loaded in the root layout.
 */
export default function StatusPage({
  variant,
  onRetry,
  detail,
}: StatusPageProps) {
  const copy = COPY[variant];

  return (
    <section
      className="it-status-page d-flex align-items-center flex-grow-1"
      aria-labelledby="status-page-title"
    >
      <div className="container-xxl py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-7">
            <div className="it-status-page__card text-center text-md-start p-4 p-md-5">
              <p className="it-status-page__code mb-2" aria-hidden>
                {copy.code}
              </p>
              <div className="d-flex flex-column flex-md-row align-items-center gap-3 mb-3">
                <span
                  className="it-status-page__icon d-inline-flex align-items-center justify-content-center"
                  aria-hidden
                >
                  <i className={`${copy.iconClass} fs-3`} />
                </span>
                <h1 id="status-page-title" className="heading-1 mb-0">
                  {copy.title}
                </h1>
              </div>
              <p className="para mb-2">{copy.description}</p>
              {detail ? (
                <p className="small text-muted mb-4">{detail}</p>
              ) : (
                <div className="mb-4" />
              )}
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center justify-content-md-start">
                {onRetry ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onRetry}
                  >
                    Riprova
                  </button>
                ) : null}
                <Link
                  href="/"
                  className={onRetry ? 'btn btn-outline-primary' : 'btn btn-primary'}
                >
                  Torna alla home
                </Link>
                <Link href="/search" className="btn btn-outline-primary">
                  Vai alla ricerca
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
